import React, { useState, useEffect } from 'react';
import { Dialog } from '../../components/ui/Dialog';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Scheme, createScheme, updateScheme } from '../../services/scheme';
import { X } from 'lucide-react';
import { showToast } from '../../utils/toast';
import { getPartnersList } from "../../services/merchant"; // adjust path
import { getCategories, Category } from "../../services/caregory"; // adjust path
import Select from 'react-select';

interface AddSchemeFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  schemeToEdit?: Scheme | null;
  token?: string | null;
}

const lenderOptions = [
  { label: "Zype", value: "Zype" },
  { label: "Cashe", value: "Cashe" },
];

const AddSchemeForm: React.FC<AddSchemeFormProps> = ({
  isOpen,
  onClose,
  onSuccess,
  schemeToEdit,
  token
}) => {
  // State for form fields
  const [name, setName] = useState('');
  const [grossTenure, setGrossTenure] = useState('');
  const [advancedEMI, setAdvancedEMI] = useState('');
  const [processingRate, setProcessingRate] = useState('');
  const [subventionCost, setSubventionCost] = useState('');
  const [annualInterest, setAnnualInterest] = useState('');
  const [interestBased, setInterestBased] = useState(false);
  const [gst, setGst] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [calOnRootAmt, setCalOnRootAmt] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);
  const [subCategories, setSubCategories] = useState<string[]>([]);
  const [newCategory, setNewCategory] = useState('');
  const [newSubCategory, setNewSubCategory] = useState('');
  const [partnerOptions, setPartnerOptions] = useState<{ label: string; value: string }[]>([]);
  const [loadingPartners, setLoadingPartners] = useState(false);
  const [isActive, setIsActive] = useState<boolean>(false);


  const [categoriesList, setCategoriesList] = useState<Category[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<any[]>([]);


  const [subCategoryOptions, setSubCategoryOptions] = useState<any[]>([]);

  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingSubCategories, setLoadingSubCategories] = useState(false);


  // Arrays for partners and lenders
  const [partners, setPartners] = useState<string[]>([]);
  const [lenders, setLenders] = useState<string[]>([]);

  // Input state for new partner and lender
  const [newPartner, setNewPartner] = useState('');
  const [newLender, setNewLender] = useState('');

  // Loading state
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Validation state
  const [errors, setErrors] = useState<{
    [key: string]: string;
  }>({});

  // Set form values if editing an existing scheme
  useEffect(() => {
    if (schemeToEdit) {
      setName(schemeToEdit.name || '');
      setGrossTenure(schemeToEdit.grossTenure?.toString() || '');
      setAdvancedEMI(schemeToEdit.advancedEMI?.toString() || '');
      setProcessingRate(schemeToEdit.processingRate?.toString() || '');
      setSubventionCost(schemeToEdit.subventionCost?.toString() || '');
      setAnnualInterest(schemeToEdit.annualInterest?.toString() || '');
      setInterestBased(schemeToEdit.interestBased || false);
      setGst(schemeToEdit.GST?.toString() || '18');
      setMinAmount(schemeToEdit.min?.toString() || '');
      setCalOnRootAmt(schemeToEdit.calOnRootAmt || false);

      // Set partners and lenders arrays
      setPartners(schemeToEdit.forPartners || []);
      setLenders(schemeToEdit.lender || []);
    } else {
      // Reset form for new scheme
      resetForm();
    }
  }, [schemeToEdit]);

  useEffect(() => {
    if (isOpen) {
      (async () => {
        setLoadingPartners(true);
        try {
          const data = await getPartnersList();
          setPartnerOptions(
            data.map((name) => ({ label: name, value: name }))
          );
        } catch (error) {
          console.error("Error loading partners:", error);
        } finally {
          setLoadingPartners(false);
        }
      })();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const fetchCategories = async () => {
      setLoadingCategories(true);
      try {
        const res = await getCategories();
        const data = res.data || [];

        setCategoriesList(data);
        setCategoryOptions(
          data.map((cat: Category) => ({
            label: cat.name,
            value: cat.code,
          }))
        );
      } catch (err) {
        console.error("Failed to fetch categories:", err);
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchCategories();
  }, [isOpen]);

  useEffect(() => {
    const fetchSubCategories = async () => {
      if (categories.length === 0) return;

      setLoadingSubCategories(true);
      try {
        const selectedCategoryCodes = categories.join(","); // e.g. "E2V,PET"
        const res = await getCategories({ categoryId: selectedCategoryCodes });

        // 🧠 Handle both API formats:
        // Case 1: res.data exists (paginated)
        // Case 2: res is an array (non-paginated for categoryId)
        const data = Array.isArray(res) ? res : res.data || [];

        // 🟢 Combine all subcategories
        const allSubCats = data.flatMap((cat: Category) => cat.subCategory || []);
        const uniqueSubs = [...new Set(allSubCats)];

        setSubCategoryOptions(uniqueSubs.map((s) => ({ label: s, value: s })));
      } catch (err) {
        console.error("Failed to fetch subcategories:", err);
      } finally {
        setLoadingSubCategories(false);
      }
    };

    fetchSubCategories();
  }, [categories]);





  // Reset form
  const resetForm = () => {
    setName('');
    setGrossTenure('');
    setAdvancedEMI('');
    setProcessingRate('');
    setSubventionCost('');
    setAnnualInterest('');
    setInterestBased(false);
    setGst('');
    setMinAmount('');
    setCalOnRootAmt(false);
    setPartners([]);
    setLenders([]);
    setNewPartner('');
    setNewLender('');
    setErrors({});
  };

  // Add partner
  const handleAddPartner = () => {
    if (newPartner.trim() && !partners.includes(newPartner.trim().toUpperCase())) {
      setPartners([...partners, newPartner.trim().toUpperCase()]);
      setNewPartner('');
    }
  };

  // Remove partner
  const handleRemovePartner = (partner: string) => {
    setPartners(partners.filter(p => p !== partner));
  };

  // Add lender
  const handleAddLender = () => {
    if (newLender.trim() && !lenders.includes(newLender.trim())) {
      setLenders([...lenders, newLender.trim()]);
      setNewLender('');
    }
  };

  // Remove lender
  const handleRemoveLender = (lender: string) => {
    setLenders(lenders.filter(l => l !== lender));
  };

  // Category handlers
  const handleAddCategory = () => {
    if (newCategory.trim() && !categories.includes(newCategory.trim().toUpperCase())) {
      setCategories([...categories, newCategory.trim().toUpperCase()]);
      setNewCategory('');
    }
  };

  const handleRemoveCategory = (category: string) => {
    setCategories(categories.filter(c => c !== category));
  };

  // Sub Category handlers
  const handleAddSubCategory = () => {
    if (newSubCategory.trim() && !subCategories.includes(newSubCategory.trim().toUpperCase())) {
      setSubCategories([...subCategories, newSubCategory.trim().toUpperCase()]);
      setNewSubCategory('');
    }
  };

  const handleRemoveSubCategory = (subCategory: string) => {
    setSubCategories(subCategories.filter(s => s !== subCategory));
  };


  // Validate form
  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!grossTenure || isNaN(Number(grossTenure)) || Number(grossTenure) <= 0) {
      newErrors.grossTenure = 'Gross tenure must be a positive number';
    }

    if (advancedEMI && (isNaN(Number(advancedEMI)) || Number(advancedEMI) < 0)) {
      newErrors.advancedEMI = 'Advanced EMI must be a non-negative number';
    }

    if (!processingRate || isNaN(Number(processingRate)) || Number(processingRate) < 0) {
      newErrors.processingRate = 'Processing rate must be a non-negative number';
    }

    if (interestBased && (!annualInterest || isNaN(Number(annualInterest)) || Number(annualInterest) < 0)) {
      newErrors.annualInterest = 'Annual interest rate is required for interest-based schemes';
    }

    if (gst && (isNaN(Number(gst)) || Number(gst) < 0)) {
      newErrors.gst = 'If provided, GST must be a non-negative number';
    }

    if (minAmount && (isNaN(Number(minAmount)) || Number(minAmount) < 0)) {
      newErrors.minAmount = 'Minimum amount must be a non-negative number';
    }

    if (lenders.length === 0) {
      newErrors.lenders = 'At least one lender is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const schemeData: any = {
        name,
        grossTenure: Number(grossTenure),
        advancedEMI: Number(advancedEMI),
        processingRate: Number(processingRate),
        subventionCost: subventionCost ? Number(subventionCost) : 0,
        interestBased,
        annualInterest: annualInterest ? Number(annualInterest) : 0,
        GST: gst ? Number(gst) : 0,
        calOnRootAmt,
        min: minAmount ? Number(minAmount) : 0,
      };

      if (schemeToEdit) {
        // -------------------- PARTNERS --------------------
        if (partners.length === 0) {
          schemeData.for = { replace: [] };
        } else if (!schemeToEdit.forPartners || schemeToEdit.forPartners.length === 0) {
          schemeData.for = { replace: partners };
        } else {
          const toAdd = partners.filter((p) => !schemeToEdit.forPartners.includes(p));
          const toRemove = schemeToEdit.forPartners.filter((p) => !partners.includes(p));
          schemeData.for = {};
          if (toAdd.length > 0) schemeData.for.add = toAdd;
          if (toRemove.length > 0) schemeData.for.remove = toRemove;
          if (Object.keys(schemeData.for).length === 0) delete schemeData.for;
        }

        // -------------------- LENDERS --------------------
        if (lenders.length === 0) {
          schemeData.lender = { replace: [] };
        } else if (!schemeToEdit.lender || schemeToEdit.lender.length === 0) {
          schemeData.lender = { replace: lenders };
        } else {
          const toAdd = lenders.filter((l) => !schemeToEdit.lender.includes(l));
          const toRemove = schemeToEdit.lender.filter((l) => !lenders.includes(l));
          schemeData.lender = {};
          if (toAdd.length > 0) schemeData.lender.add = toAdd;
          if (toRemove.length > 0) schemeData.lender.remove = toRemove;
          if (Object.keys(schemeData.lender).length === 0) delete schemeData.lender;
        }

        // -------------------- CATEGORY --------------------
        if (categories.length === 0) {
          schemeData.category = { replace: [] };
        } else if (!schemeToEdit.category || schemeToEdit.category.length === 0) {
          schemeData.category = { replace: categories };
        } else {
          const toAdd = categories.filter((c) => !schemeToEdit.category.includes(c));
          const toRemove = schemeToEdit.category.filter((c) => !categories.includes(c));
          schemeData.category = {};
          if (toAdd.length > 0) schemeData.category.add = toAdd;
          if (toRemove.length > 0) schemeData.category.remove = toRemove;
          if (Object.keys(schemeData.category).length === 0) delete schemeData.category;
        }

        // -------------------- SUB CATEGORY --------------------
        if (subCategories.length === 0) {
          schemeData.subCategory = { replace: [] };
        } else if (!schemeToEdit.subCategory || schemeToEdit.subCategory.length === 0) {
          schemeData.subCategory = { replace: subCategories };
        } else {
          const toAdd = subCategories.filter((s) => !schemeToEdit.subCategory.includes(s));
          const toRemove = schemeToEdit.subCategory.filter((s) => !subCategories.includes(s));
          schemeData.subCategory = {};
          if (toAdd.length > 0) schemeData.subCategory.add = toAdd;
          if (toRemove.length > 0) schemeData.subCategory.remove = toRemove;
          if (Object.keys(schemeData.subCategory).length === 0) delete schemeData.subCategory;
        }

        await updateScheme(schemeToEdit._id, schemeData, token);
        showToast.success('Scheme updated successfully');
      } else {
        // -------------------- CREATE MODE --------------------
        schemeData.for = partners;
        schemeData.lender = lenders;
        schemeData.category = categories;
        schemeData.subCategory = subCategories;

        await createScheme(schemeData, token);
        showToast.success('Scheme created successfully');
      }

      onSuccess();
      onClose();
      resetForm();
    } catch (error) {
      console.error('Error submitting scheme:', error);
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={schemeToEdit ? 'Edit Scheme' : 'Add New Scheme'}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="p-4">
        <div className="grid grid-cols-1 gap-4 mb-6 md:grid-cols-2">
          {/* Scheme Name */}
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">
              Scheme Name *
            </label>
            <Input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., 6by0"
              className={errors.name ? 'border-red-500' : ''}
            />
            {errors.name && (
              <p className="mt-1 text-xs text-red-500">{errors.name}</p>
            )}
          </div>

          {/* Gross Tenure */}
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">
              Gross Tenure (months) *
            </label>
            <Input
              type="number"
              value={grossTenure}
              onChange={(e) => setGrossTenure(e.target.value)}
              placeholder="e.g., 6"
              min="1"
              className={errors.grossTenure ? 'border-red-500' : ''}
            />
            {errors.grossTenure && (
              <p className="mt-1 text-xs text-red-500">{errors.grossTenure}</p>
            )}
          </div>

          {/* Advanced EMI */}
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">
              Advanced EMI
            </label>
            <Input
              type="number"
              value={advancedEMI}
              onChange={(e) => setAdvancedEMI(e.target.value)}
              placeholder="e.g., 0"
              min="0"
              className={errors.advancedEMI ? 'border-red-500' : ''}
            />
            {errors.advancedEMI && (
              <p className="mt-1 text-xs text-red-500">{errors.advancedEMI}</p>
            )}
          </div>

          {/* Processing Rate */}
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">
              Processing Rate (%) *
            </label>
            <Input
              type="number"
              value={processingRate}
              onChange={(e) => setProcessingRate(e.target.value)}
              placeholder="e.g., 1.5"
              step="0.01"
              min="0"
              className={errors.processingRate ? 'border-red-500' : ''}
            />
            {errors.processingRate && (
              <p className="mt-1 text-xs text-red-500">{errors.processingRate}</p>
            )}
          </div>

          {/* Subvention Cost */}
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">
              Subvention Cost (%)
            </label>
            <Input
              type="number"
              value={subventionCost}
              onChange={(e) => setSubventionCost(e.target.value)}
              placeholder="e.g., 2"
              step="0.01"
              min="0"
            />
          </div>

          {/* Interest Based Toggle */}
          <div>
            <label className="flex items-center mb-1 text-sm font-medium text-gray-700">
              <input
                type="checkbox"
                checked={interestBased}
                onChange={(e) => setInterestBased(e.target.checked)}
                className="mr-2 w-4 h-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
              />
              Interest Based Scheme
            </label>
          </div>

          {/* Annual Interest Rate */}
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">
              Annual Interest Rate (%)
            </label>
            <Input
              type="number"
              value={annualInterest}
              onChange={(e) => setAnnualInterest(e.target.value)}
              placeholder="e.g., 15"
              step="0.01"
              min="0"
              disabled={!interestBased}
              className={errors.annualInterest ? 'border-red-500' : ''}
            />
            {errors.annualInterest && (
              <p className="mt-1 text-xs text-red-500">{errors.annualInterest}</p>
            )}
          </div>

          {/* GST */}
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">
              GST (%)
            </label>
            <Input
              type="number"
              value={gst}
              onChange={(e) => setGst(e.target.value)}
              placeholder="e.g., 18"
              step="0.01"
              min="0"
              className={errors.gst ? 'border-red-500' : ''}
            />
            {errors.gst && (
              <p className="mt-1 text-xs text-red-500">{errors.gst}</p>
            )}
          </div>

          {/* Minimum Amount */}
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">
              Minimum Amount
            </label>
            <Input
              type="number"
              value={minAmount}
              onChange={(e) => setMinAmount(e.target.value)}
              placeholder="e.g., 6000"
              min="0"
              className={errors.minAmount ? 'border-red-500' : ''}
            />
            {errors.minAmount && (
              <p className="mt-1 text-xs text-red-500">{errors.minAmount}</p>
            )}
          </div>

          {/* Calculate on Root Amount Toggle */}
          <div>
            <label className="flex items-center mb-1 text-sm font-medium text-gray-700">
              <input
                type="checkbox"
                checked={calOnRootAmt}
                onChange={(e) => setCalOnRootAmt(e.target.checked)}
                className="mr-2 w-4 h-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
              />
              Calculate on Root Amount
            </label>
          </div>
        </div>

        {/* Partners Section */}
        <div className="mb-6">
          <label className="block mb-2 text-sm font-medium text-gray-700">
            Partner / Organizations
          </label>

          {/* 🔹 Multi-select dropdown */}
          <Select
            isMulti
            isLoading={loadingPartners}
            isSearchable
            options={partnerOptions}
            value={partners.map((p) => ({ label: p, value: p }))}
            onChange={(selected) => setPartners(selected.map((s) => s.value))}
            placeholder="Select partner(s)..."
            className="text-sm mb-2"
            styles={{
              control: (base) => ({
                ...base,
                borderColor: "#cbd5e1",
                borderRadius: "0.5rem",
                minHeight: "42px",
              }),
            }}
          />

          {/* 🔹 Display selected partners as tags */}
          <div className="flex flex-wrap gap-2 mt-2">
            {partners.map((partner, index) => (
              <div
                key={index}
                className="flex items-center px-3 py-1 text-sm bg-blue-100 rounded-full"
              >
                <span className="mr-1 text-blue-800 uppercase">{partner}</span>
                <button
                  type="button"
                  onClick={() =>
                    setPartners((prev) => prev.filter((p) => p !== partner))
                  }
                  className="text-blue-500 hover:text-blue-700"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>

          <div className="mt-2 text-xs text-gray-500">
            You can select multiple partners.
          </div>
        </div>

        {/* Lenders Section */}
        <div className="mb-6">
          <label className="block mb-2 text-sm font-medium text-gray-700">
            Lenders *
          </label>

          {/* 🔹 Dropdown for selecting lenders */}
          <Select
            isMulti
            isSearchable
            options={lenderOptions}
            value={lenders.map((l) => ({ label: l, value: l }))}
            onChange={(selected) => setLenders(selected.map((s) => s.value))}
            placeholder="Select lender(s)..."
            className="text-sm mb-2"
            styles={{
              control: (base) => ({
                ...base,
                borderColor: "#cbd5e1",
                borderRadius: "0.5rem",
                minHeight: "42px",
              }),
            }}
          />

          {/* 🔻 Validation error */}
          {errors.lenders && (
            <p className="mt-1 text-xs text-red-500">{errors.lenders}</p>
          )}

          {/* 🔹 Display selected lenders as tags */}
          <div className="flex flex-wrap gap-2 mt-2">
            {lenders.map((lender, index) => (
              <div
                key={index}
                className="flex items-center px-3 py-1 text-sm bg-yellow-100 rounded-full"
              >
                <span className="mr-1 text-yellow-800">{lender}</span>
                <button
                  type="button"
                  onClick={() => setLenders(lenders.filter((l) => l !== lender))}
                  className="text-yellow-500 hover:text-yellow-700"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Category Section */}
        <div className="mb-6">
          <label className="block mb-2 text-sm font-medium text-gray-700">
            Category
          </label>

          <Select
            isMulti
            isLoading={loadingCategories}
            options={categoryOptions}
            value={categories.map((c) => ({ label: c, value: c }))}
            onChange={(selected) => setCategories(selected.map((s) => s.value))}
            placeholder="Select category(s)..."
            className="text-sm mb-2"
          />

          <div className="flex flex-wrap gap-2 mt-2">
            {categories.map((category, index) => (
              <div
                key={index}
                className="flex items-center px-3 py-1 text-sm bg-green-100 rounded-full"
              >
                <span className="mr-1 text-green-800 uppercase">{category}</span>
                <button
                  type="button"
                  onClick={() =>
                    setCategories((prev) => prev.filter((c) => c !== category))
                  }
                  className="text-green-500 hover:text-green-700"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>


        {/* Sub Category Section */}
        <div className="mb-6">
          <label className="block mb-2 text-sm font-medium text-gray-700">
            Sub Category
          </label>

          <Select
            isMulti
            isDisabled={categories.length === 0}
            isLoading={loadingSubCategories}
            options={subCategoryOptions}
            value={subCategories.map((s) => ({ label: s, value: s }))}
            onChange={(selected) => setSubCategories(selected.map((s) => s.value))}
            placeholder={
              categories.length === 0
                ? "Select category first"
                : "Select sub category(s)..."
            }
            className="text-sm mb-2"
          />

          <div className="flex flex-wrap gap-2 mt-2">
            {subCategories.map((subCategory, index) => (
              <div
                key={index}
                className="flex items-center px-3 py-1 text-sm bg-purple-100 rounded-full"
              >
                <span className="mr-1 text-purple-800 uppercase">{subCategory}</span>
                <button
                  type="button"
                  onClick={() =>
                    setSubCategories((prev) => prev.filter((s) => s !== subCategory))
                  }
                  className="text-purple-500 hover:text-purple-700"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Active Status */}
        <div className="mb-6 flex items-center gap-3">
          <input
            id="isActive"
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
          />
          <label htmlFor="isActive" className="text-sm font-medium text-gray-700">
            Is Active
          </label>
        </div>




        {/* Form Actions */}
        <div className="flex justify-end mt-6 space-x-3">
          <Button
            type="button"
            onClick={onClose}
            variant="outline"
            className="text-gray-600"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            className="bg-indigo-600 hover:bg-indigo-700"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : (schemeToEdit ? 'Update Scheme' : 'Create Scheme')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
};

export default AddSchemeForm; 